/**
 * Ports, hulls, and the water between them.
 *
 * ── What a navy is for ────────────────────────────────────────────────────
 *
 * The strategic layer had exactly one way to get anywhere: walk. Every province
 * was a node, every road an edge, and the whole of a campaign's geography was
 * therefore *one graph*. A peninsula was a place with few neighbours. An island
 * was a place with none, which is to say a place that did not exist.
 *
 * A fleet adds a second graph over the same board, and the interest is entirely
 * in the ways the two disagree:
 *
 *   **It is faster.** A lane costs one to seven months where the road it
 *   replaces costs four hops of one to three months each. So the sea is three
 *   to seven times quicker on the same bearing — see `SEA_MONTHS_PER_STEP` in
 *   `realm.ts`, where the ratio is argued rather than assumed.
 *
 *   **It ignores the frontier.** A host aboard a ship does not pass through
 *   anybody's provinces, so it needs nobody's permission and meets nobody's
 *   garrison. There is no naval equivalent of the rule in `canMarch` that stops
 *   you walking through a neutral.
 *
 *   **It lands anywhere.** A port is needed to *leave* — hulls have to be built
 *   somewhere and men have to be got aboard somewhere — and needed nowhere to
 *   arrive. Every coast on the board is a door, and the lane network already
 *   knows nothing about ports precisely so that this asymmetry is possible.
 *
 * Those three together are what the brief means by geography being strategy. A
 * long coastline stops being frontage and becomes a liability; an island power
 * stops being a curiosity and becomes an enemy with a different shape; and the
 * Sundered Sea, which is a wall to an army, is a highway to whoever has hulls
 * on it.
 *
 * ── And what it costs ────────────────────────────────────────────────────
 *
 * Everything you own is in the same place. A fleet's fighting strength comes
 * off its hulls alone — the host aboard contributes *nothing*, because men
 * packed into transports are cargo and not soldiers — so an invasion convoy is
 * no stronger for being loaded, only far more valuable. That is the tension the
 * whole system rests on, and it is why interception at sea had to exist: the
 * answer to a fleet is a fleet, met on the water, before it arrives.
 */

import { isCoastalProvince, provinceDef } from '../../data/provinces.ts';
import { SEA_LANES } from '../../data/realm.ts';
import type { Rng } from '../../core/rng.ts';
import { armyPoints, findArmy, rawPoints, removeArmy } from './armies.ts';
import { rollBattle } from './conquest.ts';
import { standDown } from './marches.ts';
import { mayEnter } from './diplopass.ts';
import { atWar, checkElimination, logLine, nationName, provinceName } from './state.ts';
import {
  MAX_FLEETS_PER_NATION,
  MAX_FLEET_SHIPS,
  MAX_PORT,
  MIN_FLEET_SHIPS,
  PORT_GOLD,
  PORT_STONE,
  PORT_TIMBER,
  PORT_TRADE_GOLD,
  PORT_TRADE_PARTNER_CAP,
  SEA_BATTLE_DELAY,
  SHIP_CARRY,
  SHIP_GOLD,
  SHIP_POINTS,
  SHIP_TIMBER,
  type Army,
  type CampaignState,
  type Fleet,
  type SeaLane,
} from './types.ts';

// ── The lane network ───────────────────────────────────────────────────────

/**
 * The lanes as the campaign's own state, built once from the generated realm.
 *
 * A straight copy, because `SEA_LANES` carries a drawn route for the map and
 * the simulation has no business holding on to geometry. What the sim needs off
 * a lane is two ends and a duration.
 */
export function buildSeaLanes(): SeaLane[] {
  return SEA_LANES.map((l) => ({ a: l.a, b: l.b, months: l.months }));
}

export function laneBetween(c: CampaignState, p: number, q: number): SeaLane | undefined {
  const lo = Math.min(p, q);
  const hi = Math.max(p, q);
  return c.lanes.find((l) => l.a === lo && l.b === hi);
}

/** Every lane touching a province, in the order they were generated. */
export function lanesFrom(c: CampaignState, p: number): SeaLane[] {
  return c.lanes.filter((l) => l.a === p || l.b === p);
}

/** The coasts one lane away, ascending. The sea's answer to `neighbours`. */
export function seaNeighbours(c: CampaignState, p: number): number[] {
  const out: number[] = [];
  for (const l of c.lanes) {
    if (l.a === p) out.push(l.b);
    else if (l.b === p) out.push(l.a);
  }
  out.sort((x, y) => x - y);
  return out;
}

/**
 * Province-indexed lane adjacency, built in one walk.
 *
 * For the callers that want *every* province's lanes at once — the income pass
 * and the AI council — where going through `lanesFrom` per province would be
 * quadratic in the lane list for no reason. Built and thrown away by its
 * caller: holding on to it would be a cache of mutable state, and the whole
 * point of `c.lanes` being state is that it is allowed to change.
 */
export function laneAdjacency(c: CampaignState): number[][] {
  const adj: number[][] = c.provinces.map(() => []);
  for (const l of c.lanes) {
    adj[l.a].push(l.b);
    adj[l.b].push(l.a);
  }
  return adj;
}

// ── Ports ──────────────────────────────────────────────────────────────────

export function hasPort(c: CampaignState, p: number): boolean {
  return c.provinces[p].port > 0;
}

/** Every port a nation holds, ascending by province index. */
export function portsOf(c: CampaignState, nation: number): number[] {
  const out: number[] = [];
  for (let p = 0; p < c.provinces.length; p++) {
    if (c.provinces[p].owner === nation && c.provinces[p].port > 0) out.push(p);
  }
  return out;
}

/**
 * The bill for the next level of harbour works.
 *
 * Linear in what is already there, so the third wharf costs three times the
 * first. A port that got cheaper as it grew would make the answer "one enormous
 * harbour", and the answer ought to be "harbours where the sea is worth
 * something", which is a question about the map.
 */
export function portPrice(level: number): { gold: number; timber: number; stone: number } {
  const step = level + 1;
  return {
    gold: PORT_GOLD * step,
    timber: PORT_TIMBER * step,
    stone: PORT_STONE * step,
  };
}

/**
 * Whether this nation may raise harbour works here, and why not when it may not.
 *
 * Returns a sentence rather than a boolean so the world map can grey the button
 * *and* say what is wrong, which is the pattern the diplomacy panel already
 * uses. An empty string means yes.
 */
export function portRefusal(c: CampaignState, nation: number, p: number): string {
  if (p < 0 || p >= c.provinces.length) return 'There is no such place.';
  const prov = c.provinces[p];
  if (prov.owner !== nation) return `${provinceName(p)} is not yours to build on.`;
  if (!isCoastalProvince(p)) return `${provinceName(p)} has no sea to put a harbour on.`;
  if (prov.port >= MAX_PORT) return `${provinceName(p)} has all the harbour it can use.`;
  if (prov.siegeBy >= 0) return 'There is an army outside the gate.';
  const t = c.nations[nation]?.treasury;
  if (!t) return 'Nobody is paying.';
  const bill = portBill(c, nation, prov.port);
  if (t.gold < bill.gold || t.timber < bill.timber || t.stone < bill.stone) {
    return `It wants ${Math.round(bill.gold)} gold, ${bill.timber} timber and ${bill.stone} stone.`;
  }
  return '';
}

/** What a harbour actually costs this nation, planks bought in where short. */
export function portBill(
  c: CampaignState,
  nation: number,
  level: number,
): { gold: number; timber: number; stone: number } {
  const nominal = portPrice(level);
  const paid = afterSubstitution(c, nation, nominal);
  return { gold: paid.gold, timber: paid.timber, stone: nominal.stone };
}

export function tryBuildPort(c: CampaignState, nation: number, p: number): boolean {
  if (portRefusal(c, nation, p) !== '') return false;
  const prov = c.provinces[p];
  const bill = portBill(c, nation, prov.port);
  const t = c.nations[nation].treasury;
  t.gold -= bill.gold;
  t.timber -= bill.timber;
  t.stone -= bill.stone;
  prov.port++;
  return true;
}

/**
 * What a port has to trade with, counted off the lanes running out of it.
 *
 * A settled coast one lane away is a partner. A coast one lane away that has
 * built a harbour of its own is worth two, because two harbours facing each
 * other is a *route* rather than a stop.
 *
 * The doubling is the whole of the rule and it was not the first version. The
 * first version counted only other ports, which was tidier and completely
 * broken: with no harbours anywhere on the board, the first one anybody built
 * earned exactly nothing, so nobody ever built one, so nothing ever earned
 * anything. A system whose payoff requires somebody else to have gone first has
 * no first move. Counting plain coasts gives a harbour something to be worth on
 * the day it opens; doubling for ports keeps the thing the design wanted, which
 * is that a busy sea is worth more than an empty one and that both ends benefit
 * from the other end investing.
 *
 * Unowned coasts do not trade — there is nobody there to trade with — and
 * neither does anywhere under siege. A partner counts only while its owner is
 * not shooting at yours, which is the lever: declaring on a trading partner
 * costs you both, and taking their port costs them the port *and* the traffic.
 *
 * `adj` is passed in because the income pass wants this for every province at
 * once, and filtering the whole lane list per province would be quadratic.
 */
export function portPartners(c: CampaignState, p: number, adj: number[][]): number {
  const prov = c.provinces[p];
  if (prov.port <= 0 || prov.owner < 0 || prov.siegeBy >= 0) return 0;
  let n = 0;
  for (const q of adj[p]) {
    const other = c.provinces[q];
    if (other.owner < 0) continue;
    if (other.siegeBy >= 0) continue;
    if (other.owner !== prov.owner && atWar(c, prov.owner, other.owner)) continue;
    n += other.port > 0 ? 2 : 1;
  }
  return Math.min(n, PORT_TRADE_PARTNER_CAP);
}

/** What a port pays its owner this month. Zero for a province without one. */
export function portIncome(c: CampaignState, p: number, adj: number[][]): number {
  const prov = c.provinces[p];
  if (prov.port <= 0) return 0;
  return PORT_TRADE_GOLD * prov.port * portPartners(c, p, adj);
}

// ── Reading a fleet ────────────────────────────────────────────────────────

export function findFleet(c: CampaignState, id: number): Fleet | undefined {
  return c.fleets.find((f) => f.id === id);
}

export function fleetsOf(c: CampaignState, nation: number): Fleet[] {
  return c.fleets.filter((f) => f.owner === nation);
}

/** Fleets lying off a province — not under way, and therefore able to be met. */
export function fleetsAt(c: CampaignState, p: number): Fleet[] {
  return c.fleets.filter((f) => f.at === p && f.moveTo < 0);
}

export function isSailing(f: Fleet): boolean {
  return f.moveTo >= 0 && f.moveFrom >= 0;
}

/** What a fleet is worth in a fight. Hulls, and nothing but hulls. */
export function fleetPoints(f: Fleet): number {
  return f.ships * SHIP_POINTS;
}

export function fleetCapacity(f: Fleet): number {
  return f.ships * SHIP_CARRY;
}

/** Strength points already aboard. Raw points: a fleet lifts men, not veterans. */
export function cargoPoints(c: CampaignState, f: Fleet): number {
  let total = 0;
  for (const id of f.cargo) {
    const a = findArmy(c, id);
    if (a) total += rawPoints(a);
  }
  return total;
}

export function cargoArmies(c: CampaignState, f: Fleet): Army[] {
  const out: Army[] = [];
  for (const id of f.cargo) {
    const a = findArmy(c, id);
    if (a) out.push(a);
  }
  return out;
}

/**
 * How far along its lane a fleet is, 0..1. Same convention as `marchProgress`:
 * a fleet that has just cast off reads 0 and one making landfall reads near 1.
 */
export function voyageProgress(f: Fleet): number {
  if (!isSailing(f) || f.moveTotal <= 0) return 0;
  return Math.max(0, Math.min(1, (f.moveTotal - f.moveT) / f.moveTotal));
}

/** A voyage on the board, in the form the world map wants to draw it. */
export interface Voyage {
  fleetId: number;
  owner: number;
  from: number;
  to: number;
  /** 0 on casting off, 1 on the month it makes landfall. */
  progress: number;
  monthsLeft: number;
  ships: number;
  /** Armies aboard. A loaded convoy is drawn differently from an empty escort. */
  troops: number;
}

/** Every voyage currently under way, in fleet-id order. */
export function voyagesInProgress(c: CampaignState): Voyage[] {
  const out: Voyage[] = [];
  for (const f of c.fleets) {
    if (!isSailing(f)) continue;
    out.push({
      fleetId: f.id,
      owner: f.owner,
      from: f.moveFrom,
      to: f.moveTo,
      progress: voyageProgress(f),
      monthsLeft: f.moveT,
      ships: f.ships,
      troops: f.cargo.length,
    });
  }
  out.sort((x, y) => x.fleetId - y.fleetId);
  return out;
}

/** A one-line composition summary for the world-map panel. */
export function fleetSummary(c: CampaignState, f: Fleet): string {
  const hulls = `${f.ships} ${f.ships === 1 ? 'hull' : 'hulls'}`;
  if (f.cargo.length === 0) return `${hulls}, empty`;
  const men = Math.round(cargoPoints(c, f));
  return `${hulls}, carrying ${f.cargo.length === 1 ? 'a host' : `${f.cargo.length} hosts`} (${men})`;
}

// ── Raising and paying off ─────────────────────────────────────────────────

/**
 * Timber you have not got is bought from somebody who has, and it costs.
 *
 * The same substitution `armies.ts` makes for iron, made for the same reason
 * and after the same failure. Timber is a gate on hulls exactly as iron was a
 * gate on soldiers, and the file that introduced `GOLD_PER_MISSING_IRON` states
 * the lesson plainly: *a strategic resource that can eliminate a nation from
 * the game by arithmetic is not a strategic resource, it is a bug with a
 * theme.* It was true a second time here, and worse — a crown seated entirely
 * on marsh and moor with a hundred leagues of coastline could not put a single
 * hull on the water, ever, on any seed. Its whole strategic character was
 * decided by a resource table it had no way to argue with.
 *
 * So timber makes ships cheaper rather than possible. A forested crown builds a
 * navy at a discount, which is a real advantage; a treeless one buys its planks
 * in and pays through the nose, which is a real cost. Neither is a wall.
 */
export const GOLD_PER_MISSING_TIMBER = 4;

/** The nominal bill: what it costs before anything is substituted. */
export function fleetPrice(ships: number): { gold: number; timber: number } {
  return { gold: Math.round(ships * SHIP_GOLD), timber: Math.round(ships * SHIP_TIMBER) };
}

/** What a bill actually comes to for this nation, after buying in the shortfall. */
export function afterSubstitution(
  c: CampaignState,
  nation: number,
  nominal: { gold: number; timber: number },
): { gold: number; timber: number } {
  const have = c.nations[nation]?.treasury.timber ?? 0;
  const timber = Math.min(have, nominal.timber);
  return { gold: nominal.gold + (nominal.timber - timber) * GOLD_PER_MISSING_TIMBER, timber };
}

/**
 * The largest fleet this nation could actually float right now, so the world map
 * can put a real number on the button rather than a nominal one.
 *
 * Two branches with different slopes, because below the timber ceiling the bill
 * is flat gold and above it every missing plank is bought in at a premium. The
 * headroom is the rounding in `fleetPrice`, kept back for the reason
 * `maxAffordablePoints` keeps its own: a number that is occasionally one gold
 * too dear is refused at the till, and "build the largest fleet you can afford"
 * that reliably builds nothing is worse than no button.
 */
export function maxAffordableShips(c: CampaignState, nation: number): number {
  const t = c.nations[nation]?.treasury;
  if (!t) return 0;
  const gold = Math.max(0, t.gold - (0.5 + 0.5 * GOLD_PER_MISSING_TIMBER));
  const covered = gold / SHIP_GOLD;
  const timberCeiling = t.timber / SHIP_TIMBER;
  const byPurse =
    covered <= timberCeiling
      ? covered
      : (gold + t.timber * GOLD_PER_MISSING_TIMBER) /
        (SHIP_GOLD + SHIP_TIMBER * GOLD_PER_MISSING_TIMBER);
  return Math.max(0, Math.min(Math.floor(byPurse), MAX_FLEET_SHIPS));
}

export function fleetRefusal(c: CampaignState, nation: number, p: number, ships: number): string {
  if (p < 0 || p >= c.provinces.length) return 'There is no such place.';
  const prov = c.provinces[p];
  if (prov.owner !== nation) return `${provinceName(p)} is not yours.`;
  if (prov.port <= 0) return `${provinceName(p)} has no harbour to build in.`;
  if (prov.siegeBy >= 0) return 'The yards are under siege.';
  if (fleetsOf(c, nation).length >= MAX_FLEETS_PER_NATION) {
    return 'There are no more crews to be had.';
  }
  const want = Math.max(MIN_FLEET_SHIPS, Math.min(MAX_FLEET_SHIPS, Math.round(ships)));
  const t = c.nations[nation]?.treasury;
  if (!t) return 'Nobody is paying.';
  const bill = afterSubstitution(c, nation, fleetPrice(want));
  if (t.gold < bill.gold || t.timber < bill.timber) {
    return `${want} hulls want ${Math.round(bill.gold)} gold and ${bill.timber} timber.`;
  }
  return '';
}

/**
 * Lay down hulls. Returns null and spends nothing when it cannot be afforded, so
 * the player's command and the AI's council go through identical rules — the
 * same discipline `tryRaiseArmy` keeps, and for the same reason.
 */
export function tryRaiseFleet(
  c: CampaignState,
  nation: number,
  p: number,
  ships: number,
): Fleet | null {
  const want = Math.max(MIN_FLEET_SHIPS, Math.min(MAX_FLEET_SHIPS, Math.round(ships)));
  if (fleetRefusal(c, nation, p, want) !== '') return null;
  const bill = afterSubstitution(c, nation, fleetPrice(want));
  const t = c.nations[nation].treasury;
  t.gold -= bill.gold;
  t.timber -= bill.timber;
  return makeFleet(c, nation, p, want);
}

/**
 * Add hulls to a fleet already lying in one of its owner's harbours.
 *
 * The naval half of `tryReinforceArmy`, and needed for the same reason that one
 * is: without it a crown's only way to put more ships on the water is another
 * fleet, and `MAX_FLEETS_PER_NATION` then caps a navy at three of whatever size
 * it could afford on the three months it happened to be flush. Measured, that
 * came to three two-hull squadrons apiece — thirty fleets on the board and not
 * one of them able to lift a host or win a fight.
 *
 * Returns the hulls actually laid down, which may be fewer than asked for and
 * is zero when nothing could be paid for.
 */
export function tryAddHulls(
  c: CampaignState,
  nation: number,
  fleetId: number,
  ships: number,
): number {
  const f = findFleet(c, fleetId);
  if (!f || f.owner !== nation) return 0;
  if (isSailing(f)) return 0;
  const prov = c.provinces[f.at];
  if (prov.owner !== nation || prov.port <= 0 || prov.siegeBy >= 0) return 0;

  const want = Math.min(Math.round(ships), MAX_FLEET_SHIPS - f.ships);
  if (want < 1) return 0;
  const bill = afterSubstitution(c, nation, fleetPrice(want));
  const t = c.nations[nation]?.treasury;
  if (!t || t.gold < bill.gold || t.timber < bill.timber) return 0;
  t.gold -= bill.gold;
  t.timber -= bill.timber;
  f.ships += want;
  return want;
}

export function makeFleet(c: CampaignState, owner: number, at: number, ships: number): Fleet {
  const f: Fleet = {
    id: c.nextFleetId++,
    owner,
    at,
    moveTo: -1,
    moveFrom: -1,
    moveT: 0,
    moveTotal: 0,
    intercepting: -1,
    ships,
    cargo: [],
    name: fleetName(c, owner, at),
  };
  c.fleets.push(f);
  return f;
}

/** Fleets are named for the harbour that built them, and counted per nation. */
function fleetName(c: CampaignState, owner: number, at: number): string {
  const n = c.fleets.filter((f) => f.owner === owner).length + 1;
  return `${ORDINALS[n - 1] ?? `${n}th`} Fleet of ${provinceDef(at).name}`;
}

const ORDINALS = ['First', 'Second', 'Third', 'Fourth', 'Fifth', 'Sixth'];

/**
 * Pay off a fleet. Anything aboard is put ashore where the hulls lie first —
 * disbanding a loaded convoy must never be a way to delete an army by accident,
 * and it must never be a way to teleport one either, which is why it is refused
 * outright while under way.
 */
export function disbandFleet(c: CampaignState, f: Fleet): boolean {
  if (isSailing(f)) return false;
  putAshore(c, f);
  removeFleet(c, f.id);
  return true;
}

export function removeFleet(c: CampaignState, id: number): void {
  const i = c.fleets.findIndex((f) => f.id === id);
  if (i >= 0) c.fleets.splice(i, 1);
}

// ── Getting aboard, and getting off ────────────────────────────────────────

export function embarkRefusal(c: CampaignState, f: Fleet, a: Army): string {
  if (isSailing(f)) return `${f.name} is already at sea.`;
  if (a.aboard >= 0) return `${a.name} is already aboard.`;
  if (a.owner !== f.owner) return 'They do not answer to the same crown.';
  if (a.moveTo >= 0) return `${a.name} is on the road.`;
  if (a.at !== f.at) return `${a.name} is not at ${provinceName(f.at)}.`;
  if (c.provinces[f.at].port <= 0) return `${provinceName(f.at)} has no harbour to load at.`;
  if (c.provinces[f.at].owner !== f.owner) return 'The harbour is not yours.';
  if (rawPoints(a) > fleetCapacity(f) - cargoPoints(c, f)) {
    return `${f.name} has not the hulls to lift them.`;
  }
  return '';
}

export function embarkArmy(c: CampaignState, f: Fleet, a: Army): boolean {
  if (embarkRefusal(c, f, a) !== '') return false;
  a.aboard = f.id;
  a.stance = 0;
  a.siegeTurns = 0;
  a.intercepting = -1;
  f.cargo.push(a.id);
  return true;
}

/**
 * Whether this fleet may put its cargo ashore where it lies.
 *
 * Any coast will do, and that is the rule the whole system turns on. A port is
 * needed to *leave* — hulls are built in one and men are got aboard in one —
 * and needed nowhere at all to arrive, so every coastal province on the board is
 * a door somebody could come through. What you may not do is put men ashore on
 * ground you have no right to stand on, which is the same question `canMarch`
 * asks about a road and is answered by the same predicate: a landing is either a
 * homecoming, an invitation, or an invasion.
 *
 * The *sea* is never asked for permission. `sailRefusal` lets any hull onto any
 * lane, because open water belongs to nobody and a navy that needed leave to
 * cross a channel would put the whole board behind one crown's veto. It is the
 * shore that has an owner — which is also why a grant of passage to a crown with
 * hulls is a far larger concession than the same grant to a landlocked one: a
 * road grant opens the border you share, and a fleet turns it into every coast
 * you own.
 */
export function landingRefusal(c: CampaignState, f: Fleet): string {
  if (isSailing(f)) return `${f.name} is still at sea.`;
  if (f.cargo.length === 0) return 'There is nobody aboard.';
  const prov = c.provinces[f.at];
  if (!isCoastalProvince(f.at)) return `${provinceName(f.at)} has no shore to land on.`;
  if (!mayEnter(c, f.owner, f.at)) {
    return `${nationName(c, prov.owner)} has not invited them ashore.`;
  }
  return '';
}

/**
 * Put everything aboard onto the beach. Returns how many hosts landed.
 *
 * The armies simply appear standing in the province, which means the ordinary
 * `Battles` phase picks them up this same month and a landing on hostile ground
 * becomes a fight through exactly the same path a march would have. Nothing
 * about a landing is special once the men are ashore, and nothing should be.
 */
export function disembarkFleet(c: CampaignState, f: Fleet): number {
  if (landingRefusal(c, f) !== '') return 0;
  return putAshore(c, f);
}

/** The unconditional half of `disembarkFleet`, shared with disbanding. */
function putAshore(c: CampaignState, f: Fleet): number {
  const landed = cargoArmies(c, f);
  for (const a of landed) {
    a.aboard = -1;
    // A host walking off a hull has no land order, and any it was carrying when
    // it embarked was for a road on the far side of an ocean. `standDown` drops
    // it rather than leaving a destination nothing will ever act on.
    standDown(a, f.at);
    // Men who have been at sea a month arrive hungry, whoever's beach it is.
    a.supply = Math.min(a.supply, 0.7);
  }
  f.cargo = [];
  return landed.length;
}

/**
 * Everything aboard goes to the bottom with the hulls.
 *
 * Only reached when a fleet is destroyed outright at sea. A fleet that merely
 * loses a fight keeps whatever hulls it has left and carries its survivors home
 * — see `applySeaLosses` — because "you lost the naval engagement" and "your
 * entire expeditionary army no longer exists" should not be the same sentence
 * unless the last ship actually went down.
 */
function drown(c: CampaignState, f: Fleet): void {
  for (const a of cargoArmies(c, f)) {
    logLine(c, `${a.name} goes down with ${f.name}. The sea keeps them.`);
    removeArmy(c, a.id);
  }
  f.cargo = [];
}

// ── Sailing ────────────────────────────────────────────────────────────────

export function sailRefusal(c: CampaignState, f: Fleet, to: number): string {
  if (isSailing(f)) return `${f.name} is already under way.`;
  if (to === f.at) return 'They are already there.';
  if (to < 0 || to >= c.provinces.length) return 'There is no such coast.';
  if (!laneBetween(c, f.at, to)) {
    return `No lane runs from ${provinceName(f.at)} to ${provinceName(to)}.`;
  }
  return '';
}

/** Months the voyage would take, or 0 if it is not a legal one. */
export function sailMonths(c: CampaignState, f: Fleet, to: number): number {
  if (sailRefusal(c, f, to) !== '') return 0;
  return laneBetween(c, f.at, to)?.months ?? 0;
}

export function orderSail(c: CampaignState, f: Fleet, to: number): boolean {
  const months = sailMonths(c, f, to);
  if (months <= 0) return false;
  f.moveFrom = f.at;
  f.moveTo = to;
  f.moveT = months;
  f.moveTotal = months;
  // A fleet that casts off stops chasing whatever it was chasing. Both are
  // orders for the coming month and a fleet has one month.
  f.intercepting = -1;
  return true;
}

/**
 * Turn a voyage around. The lane is reversed rather than cancelled, exactly as
 * `haltMarch` does it and for the same reason: a crossing you can un-order for
 * nothing is a crossing that cost nothing to order.
 */
export function haltVoyage(f: Fleet): boolean {
  if (!isSailing(f)) return false;
  const covered = f.moveTotal - f.moveT;
  const home = f.moveFrom;
  f.moveFrom = f.moveTo;
  f.moveTo = home;
  f.moveT = covered;
  f.intercepting = -1;
  if (f.moveT <= 0) berth(f, home);
  return true;
}

/** Put a fleet down off a coast and clear everything about its voyage. */
export function berth(f: Fleet, province: number): void {
  f.at = province;
  f.moveTo = -1;
  f.moveFrom = -1;
  f.moveT = 0;
  f.moveTotal = 0;
  f.intercepting = -1;
}

/**
 * Advance every voyage by a month, landing the ones that arrive.
 *
 * Cargo is *not* put ashore automatically. Making landfall and going ashore are
 * two decisions, and keeping them apart is what lets a fleet arrive off a
 * hostile coast, look at what is standing there, and sail somewhere else — which
 * is most of what having ships is for.
 */
export function advanceVoyages(c: CampaignState): void {
  for (const f of c.fleets) {
    if (!isSailing(f)) continue;
    f.moveT--;
    if (f.moveT > 0) continue;
    const to = f.moveTo;
    berth(f, to);
    // The hosts aboard have been carried, not marched: their `at` follows the
    // hull so that putting them ashore lands them where the hull actually is.
    for (const a of cargoArmies(c, f)) a.at = to;
    // The player's own landfalls only. The chronicle is forty-eight lines and
    // somebody else's shipping news is what it is least able to afford — see
    // the note in `ai.ts`'s `layDownHulls`.
    if (f.owner === c.playerNation) logLine(c, `${f.name} makes ${provinceName(to)}.`);
  }
  // A chase that was never resolved is over: it was an order for this month.
  for (const f of c.fleets) f.intercepting = -1;
}

// ── Meeting at sea ─────────────────────────────────────────────────────────

/**
 * Whether `f` may set off after `target`.
 *
 * A fleet may run down a voyage on any lane whose ends are within one lane of
 * the coast it lies off — its own doorstep, or one hop of open water from it.
 * Being in the right water is what buys the interception, which keeps it a
 * positional advantage the player can see on the map rather than a statistic.
 *
 * ── Why the sortie range, when a march has none ─────────────────────────────
 *
 * Interception on land is exact: you catch a host on a road that touches the
 * province you stand in, and nothing looser. That works because armies are
 * everywhere — a border has hosts strung along it and the roads are short.
 *
 * At sea it produced nothing whatsoever. Measured over eight campaigns and a
 * hundred and twenty months: thirty fleet-months of loaded convoys crossing
 * open water and not one interception ordered, by anybody, ever. The reason is
 * structural rather than a tuning miss. A fleet berths in a *harbour*, and an
 * invasion sails at a *coast* — and the coast somebody wants to land on is
 * almost never the harbour you keep your ships in, because a harbour is
 * defended and that is the point of it. The exact rule therefore asked the
 * defender to have guessed the precise province, out of every coast they hold,
 * that the attacker would aim at.
 *
 * One lane of slack turns that into the decision it was always meant to be:
 * keep a fleet in the neighbourhood of a threatened coast and you can answer a
 * landing; keep none and you cannot. It is the same reasoning `SCOUT_RANGE`
 * records for sight — a range is a rule the player can read off the map, and
 * one hop is the range at which the answer to "why could I not stop that" is
 * always "look at where your ships were".
 */
export function canInterceptFleet(c: CampaignState, f: Fleet, target: Fleet): boolean {
  if (f.id === target.id) return false;
  if (isSailing(f)) return false;
  if (f.ships <= 0 || target.ships <= 0) return false;
  if (!isSailing(target)) return false;
  if (f.owner === target.owner) return false;
  if (!atWar(c, f.owner, target.owner)) return false;
  return withinSortie(c, f.at, target.moveFrom) || withinSortie(c, f.at, target.moveTo);
}

/** The coast itself, or one lane from it. */
function withinSortie(c: CampaignState, from: number, end: number): boolean {
  return end === from || laneBetween(c, from, end) !== undefined;
}

export function orderFleetIntercept(c: CampaignState, f: Fleet, target: Fleet): boolean {
  if (!canInterceptFleet(c, f, target)) return false;
  f.intercepting = target.id;
  return true;
}

/** Two fleets about to meet, and the lane they meet on. */
export interface SeaContact {
  attacker: Fleet;
  defender: Fleet;
  from: number;
  to: number;
  /** Fleet id that went out to force this, or -1 when the two simply met. */
  interceptor: number;
}

/**
 * Find the next meeting on the water, or null.
 *
 * Ordered interceptions first, then head-on crossings — the same precedence
 * `nextRoadContact` keeps, and for the same reason: a decision somebody made
 * should not be pre-empted by an accident. Fleets are walked in id order so
 * which contact happens first is a fact about the board rather than about
 * iteration luck.
 */
export function nextSeaContact(c: CampaignState): SeaContact | null {
  const chasers = c.fleets
    .filter((f) => f.intercepting >= 0 && !isSailing(f) && f.ships > 0)
    .sort((x, y) => x.id - y.id);
  for (const chaser of chasers) {
    const target = findFleet(c, chaser.intercepting);
    if (!target || !canInterceptFleet(c, chaser, target)) {
      chaser.intercepting = -1;
      continue;
    }
    return {
      attacker: chaser,
      defender: target,
      from: target.moveFrom,
      to: target.moveTo,
      interceptor: chaser.id,
    };
  }

  const sailing = c.fleets.filter((f) => isSailing(f) && f.ships > 0).sort((x, y) => x.id - y.id);
  for (let i = 0; i < sailing.length; i++) {
    for (let j = i + 1; j < sailing.length; j++) {
      const a = sailing[i];
      const b = sailing[j];
      if (a.owner === b.owner) continue;
      if (!atWar(c, a.owner, b.owner)) continue;
      if (a.moveFrom !== b.moveTo || a.moveTo !== b.moveFrom) continue;
      return { attacker: a, defender: b, from: a.moveFrom, to: a.moveTo, interceptor: -1 };
    }
  }

  /**
   * The blockade: a war fleet in harbour contests the water at its own door,
   * and does it without waiting to be told.
   *
   * This is the rule that makes a navy defensive, and it had to exist because
   * an *ordered* interception can almost never be given in time. Orders are
   * issued in `Council`, at the end of a month; a convoy also sets sail in
   * `Council`; and the median lane on this board is one month long. So a
   * chase can only be ordered against a voyage that had already begun when the
   * order was given, which for a one-month crossing means the quarry has
   * already arrived. Measured over eight campaigns: thirty fleet-months of
   * loaded convoys and not one interception, before this existed.
   *
   * So a fleet lying at either end of a lane engages what crosses it. Three
   * conditions, and each is doing work:
   *
   *   **Empty.** A loaded transport does not go looking for a fight. It is
   *   carrying the thing the fight would put at risk.
   *
   *   **The exact lane end.** Not the sortie range `canInterceptFleet` allows —
   *   that is the range at which you may *commit* your month to a chase, and
   *   committing is what an order is for. This is the water a fleet is already
   *   sitting in.
   *
   *   **Not hopelessly outmatched.** A two-hull picket does not throw itself at
   *   a nine-hull convoy, because an engagement nobody could survive is not a
   *   blockade, it is a way of losing your navy to somebody else's decision.
   *   Below the threshold it stays in harbour and watches, which is a real and
   *   readable consequence of having built too small.
   */
  const pickets = c.fleets
    .filter((f) => !isSailing(f) && f.ships > 0 && f.cargo.length === 0)
    .sort((x, y) => x.id - y.id);
  for (const picket of pickets) {
    for (const quarry of sailing) {
      if (picket.owner === quarry.owner) continue;
      if (!atWar(c, picket.owner, quarry.owner)) continue;
      if (quarry.moveFrom !== picket.at && quarry.moveTo !== picket.at) continue;
      if (fleetPoints(picket) * BLOCKADE_NERVE < fleetPoints(quarry)) continue;
      return {
        attacker: picket,
        defender: quarry,
        from: quarry.moveFrom,
        to: quarry.moveTo,
        // An interceptor, because that is exactly what it is: it goes out from
        // a coast and, if it wins, it goes back to it.
        interceptor: picket.id,
      };
    }
  }
  return null;
}

/**
 * How outmatched a picket will let itself be before it stays in harbour.
 *
 * 1.15 rather than 1.0, so a fleet slightly the weaker still fights — a
 * blockade that only ever engaged when it was certain to win would never be
 * run, since the convoy could simply build one more hull than the picket.
 */
const BLOCKADE_NERVE = 1.15;

/**
 * Fight one meeting at sea, all the way through, now.
 *
 * A sea battle never becomes a `PendingBattle` and never suspends the turn —
 * see the note where `BattleKind.Sea` would have been. It is settled by the
 * shared `rollBattle` on the two hull counts, which means a convoy's escort is
 * the only thing standing between the host aboard and the bottom.
 */
export function resolveSeaContact(c: CampaignState, rng: Rng, contact: SeaContact): void {
  const { attacker, defender } = contact;
  const atk = Math.max(1, Math.round(fleetPoints(attacker)));
  const def = Math.max(1, Math.round(fleetPoints(defender)));
  const roll = rollBattle(rng, atk, def);

  const winner = roll.attackerWon ? attacker : defender;
  const loser = roll.attackerWon ? defender : attacker;
  const winnerSurvive = roll.attackerWon ? roll.attackerSurvive : roll.defenderSurvive;
  const loserSurvive = roll.attackerWon ? roll.defenderSurvive : roll.attackerSurvive;

  applySeaLosses(c, winner, winnerSurvive);
  applySeaLosses(c, loser, loserSurvive);

  // One line, not two. A fight at sea is worth a line of the player's
  // forty-eight; it is not worth two of them.
  logLine(
    c,
    `${attacker.name} meets ${defender.name} off ${provinceName(contact.to)} — ` +
      `${atk} against ${def}, and ${nationName(c, winner.owner)} holds the water.`,
  );

  settleSeaBattle(c, winner, loser, contact.interceptor);
  checkElimination(c);
}

/**
 * Hulls sink, and what they were carrying takes the same beating.
 *
 * The cargo shares the survival fraction rather than being spared or drowned
 * outright, because a convoy that is caught should bleed men in proportion to
 * the hulls it lost — troops go into the water with the transports that were
 * carrying them. Only when the last hull is gone does the rest of the host go
 * with it.
 */
function applySeaLosses(c: CampaignState, f: Fleet, survive: number): void {
  const keep = Math.max(0, Math.min(1, survive));
  f.ships = Math.floor(f.ships * keep);
  if (f.ships <= 0) {
    f.ships = 0;
    drown(c, f);
    logLine(c, `${f.name} is broken up on the water. Nothing comes home.`);
    removeFleet(c, f.id);
    return;
  }
  for (const a of cargoArmies(c, f)) {
    const before = armyModelCount(a);
    for (const r of a.regiments) r.models = Math.floor(r.models * keep);
    a.regiments = a.regiments.filter((r) => r.models > 0);
    const lost = before - armyModelCount(a);
    if (lost > 0) logLine(c, `${a.name} loses ${lost} in the water.`);
    if (a.regiments.length === 0) {
      // Nobody left of that host. It stops being aboard by stopping existing.
      f.cargo = f.cargo.filter((id) => id !== a.id);
      removeArmy(c, a.id);
    }
  }
  // Hulls the fleet no longer has cannot carry what it still has aboard. The
  // overflow is the men who could not be got off the sinking transports.
  while (cargoPoints(c, f) > fleetCapacity(f) && f.cargo.length > 0) {
    const id = f.cargo[f.cargo.length - 1];
    const a = findArmy(c, id);
    f.cargo.pop();
    if (a) {
      logLine(c, `${a.name} had no hull left to stand on.`);
      removeArmy(c, a.id);
    }
  }
}

function armyModelCount(a: Army): number {
  let n = 0;
  for (const r of a.regiments) n += r.models;
  return n;
}

/**
 * What a sea battle does to the voyages that produced it.
 *
 * The loser puts about for the coast it sailed from, arriving immediately — it
 * has spent the month running. The winner keeps its lane and pays
 * `SEA_BATTLE_DELAY` months for having stopped to fight, and an interceptor that
 * wins returns to the coast it went out from. That last is what stops
 * interception being a free extra move: catching a fleet costs you your month
 * too, which is the same bargain `settleRoadBattle` strikes on land.
 */
export function settleSeaBattle(
  c: CampaignState,
  winner: Fleet,
  loser: Fleet,
  interceptorId: number,
): void {
  if (loser.ships > 0) {
    const home = isSailing(loser) ? loser.moveFrom : loser.at;
    berth(loser, home);
    for (const a of cargoArmies(c, loser)) a.at = home;
  }
  if (winner.ships > 0) {
    if (winner.id === interceptorId) {
      berth(winner, winner.at);
    } else if (isSailing(winner)) {
      winner.moveT += SEA_BATTLE_DELAY;
      winner.moveTotal += SEA_BATTLE_DELAY;
    }
  }
  const chaser = interceptorId >= 0 ? findFleet(c, interceptorId) : undefined;
  if (chaser) chaser.intercepting = -1;
}

// ── Housekeeping the turn machine wants ────────────────────────────────────

/**
 * Put right anything the rest of the month may have broken.
 *
 * Two things can go wrong and both are ordinary rather than exceptional. A host
 * aboard a fleet that no longer exists — because every hull sank in a battle
 * this same month — is a host with a dangling `aboard`, and it must not be left
 * pointing at nothing where `armiesStandingIn` will keep filtering it off the
 * board forever. And a fleet whose harbour changed hands under it is a fleet
 * with nowhere to load, which is a strategic problem rather than a bug: it keeps
 * its hulls and loses its base, exactly as it should.
 */
export function reconcileFleets(c: CampaignState): void {
  const live = new Set(c.fleets.map((f) => f.id));
  for (const a of c.armies) {
    if (a.aboard < 0) continue;
    if (live.has(a.aboard)) continue;
    a.aboard = -1;
    standDown(a, a.at);
  }
  for (const f of c.fleets) {
    f.cargo = f.cargo.filter((id) => {
      const a = findArmy(c, id);
      return a !== undefined && a.aboard === f.id;
    });
  }
}

/**
 * A hostile convoy bound for a coast this nation holds, if there is one.
 *
 * The warning list wants this, and it is the whole of the fairness repair for
 * ships: a landing has to be visible for the length of the voyage, because a
 * fleet is three to seven times faster than a road and a host that could appear
 * on an unwatched coast would break the rule `warnings.ts` exists to keep.
 */
export function threateningVoyages(c: CampaignState, nation: number): Fleet[] {
  return c.fleets
    .filter(
      (f) =>
        isSailing(f) &&
        f.cargo.length > 0 &&
        f.owner !== nation &&
        atWar(c, nation, f.owner) &&
        c.provinces[f.moveTo].owner === nation,
    )
    .sort((x, y) => x.id - y.id);
}

/** Total hull upkeep a nation owes this month. */
export function fleetUpkeep(c: CampaignState, nation: number): number {
  let hulls = 0;
  for (const f of c.fleets) if (f.owner === nation) hulls += f.ships;
  return hulls;
}

/** Strength points a nation currently has at sea, for the AI and the panel. */
export function seaStrength(c: CampaignState, nation: number): number {
  let total = 0;
  for (const f of c.fleets) if (f.owner === nation) total += fleetPoints(f);
  return total;
}

/** Points of army a nation could lift at once, across every fleet it floats. */
export function liftCapacity(c: CampaignState, nation: number): number {
  let total = 0;
  for (const f of c.fleets) if (f.owner === nation) total += fleetCapacity(f);
  return total;
}

/** The strongest army a fleet is carrying, for the map's counter label. */
export function heaviestCargo(c: CampaignState, f: Fleet): Army | undefined {
  let best: Army | undefined;
  for (const a of cargoArmies(c, f)) {
    if (!best || armyPoints(a) > armyPoints(best)) best = a;
  }
  return best;
}
